<template>
  <div class="editor has-background-white">
    <div class="editor__header">
      <div class="editor__title">
        Flow Definition
      </div>
      <b-button
        v-if="canSubmit"
        :loading="submitting"
        size="is-small"
        type="is-primary"
        @click="updateDefinitionHandler"
      >
        Update Flow
      </b-button>
    </div>
    <div class="editor__body">
      <mc-split-view
        container-class="editor__tabs"
        :has-right-header="!!selectedPromptKey"
        :height="splitViewHeight"
        :left-flex="1"
        :right-flex="2"
      >
        <template #leftHeader>
          <div class="header-title">
            Prompts
          </div>
          <b-button
            outlined
            size="is-small"
            type="is-primary"
            @click="isSharedAnswersOpen = true"
          >
            Shared Answers
          </b-button>
        </template>
        <template #leftBody>
          <div
            v-if="promptKeys.length === 0"
            class="editor__hint has-text-grey"
          >
            <b-icon icon="information" />
            <div class="content">
              Get started by adding prompts
            </div>
          </div>
          <fragment v-else>
            <div
              v-for="promptKey in promptKeys"
              :key="promptKey"
              :class="[
                'editor__key',
                {
                  'editor__key--active': selectedPromptKey === promptKey
                }
              ]"
              @click="selectedPromptKey = promptKey"
            >
              <div class="editor__key-title">
                {{ promptKey }}
              </div>
              <div
                v-if="selectedPromptKey !== promptKey"
                class="editor__key-trailer has-text-grey"
              >
                {{ prompts[promptKey].message }}
              </div>
              <a @click.stop="deletePromptHandler(promptKey)">
                <b-icon icon="delete-outline" type="is-danger" />
              </a>
              <b-icon icon="chevron-right" />
            </div>
          </fragment>
          <form class="editor__add-prompt" @submit.prevent="addPromptHandler">
            <b-field v-if="isAddingPrompt">
              <b-input
                v-model="newPromptKey"
                v-bfocus="true"
                expanded
                placeholder="Prompt Key"
                type="text"
                icon="key"
              />
              <p class="control">
                <button class="button" native-type="submit">
                  <b-icon icon="check" size="is-small" />
                </button>
              </p>
              <p class="control">
                <button class="button" @click="isAddingPrompt = false">
                  <b-icon icon="close" size="is-small" />
                </button>
              </p>
            </b-field>
            <b-button
              v-else
              class="is-fullwidth"
              type="is-light"
              @click="isAddingPrompt = true"
            >
              Add Prompt
            </b-button>
          </form>
        </template>
        <template v-if="selectedPromptKey" #rightHeader>
          <form class="header-title" @submit.prevent="editKeyHandler">
            <b-field v-if="isEditingKey">
              <b-input
                v-model="editingPromptKey"
                v-bfocus="true"
                placeholder="Prompt Key"
                type="text"
                icon="key"
                size="is-small"
              />
              <p class="control">
                <button class="button is-small" native-type="submit">
                  <b-icon icon="check" size="is-small" />
                </button>
              </p>
              <p class="control">
                <button class="button is-small" @click="isEditingKey = false">
                  <b-icon icon="close" size="is-small" />
                </button>
              </p>
            </b-field>
            <fragment v-else>
              <span>{{ selectedPromptKey }}</span>
              <a class="editor__edit-key" @click="openKeyEditor">
                <b-icon icon="pencil" size="is-small" />
              </a>
            </fragment>
          </form>
        </template>
        <template #rightBody>
          <prompt-editor
            v-if="selectedPromptKey"
            :key="selectedPromptKey"
            :definition="definition"
            :prompt-key="selectedPromptKey"
            @change="updatePromptHandler"
          />
          <div v-else class="editor__hint has-text-grey">
            <b-icon icon="information" />
            <div class="content">
              Select a prompt to edit it!
            </div>
          </div>
        </template>
      </mc-split-view>
      <shared-answers
        :definition="definition"
        :height="splitViewHeight"
        :on-answer-id-change="updateSharedAnswerIdHandler"
        :open="isSharedAnswersOpen"
        :prompt-keys="promptKeys"
        @change="definition = $event"
        @close="isSharedAnswersOpen = false"
      />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import forOwn from 'lodash/forOwn';

import PromptEditor from './PromptEditor';
import regex from '../../utils/regex';
import SharedAnswers from './SharedAnswers';

// TODO: Make responsive
export default {
  components: {
    PromptEditor,
    SharedAnswers
  },
  props: {
    flowDefinition: {
      type: Object,
      default: function() {
        return {
          answers: {},
          prompts: {}
        };
      }
    },
    splitViewHeight: {
      type: String,
      default: 'calc(100vh - 156px)'
    },
    onSubmit: Function,
    submitting: Boolean
  },
  data: function() {
    return {
      definition: {
        answers: {},
        prompts: {}
      },
      isAddingPrompt: false,
      isEditingKey: false,
      isSharedAnswersOpen: false,
      editingPromptKey: '',
      newPromptKey: '',
      selectedPromptKey: null
    };
  },
  computed: {
    answers() {
      return this.definition.answers;
    },
    canSubmit() {
      return this.onSubmit && typeof this.onSubmit === 'function';
    },
    hasPrompts() {
      return this.promptKeys.length > 0;
    },
    hasStartPrompt() {
      return this.prompts.hasOwnProperty('start');
    },
    promptKeys() {
      return Object.keys(this.prompts);
    },
    prompts() {
      return this.definition.prompts;
    }
  },
  watch: {
    flowDefinition: {
      handler() {
        this.definition = cloneDeep(this.flowDefinition);
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    addPromptHandler() {
      if (!this.isKeyValid(this.newPromptKey)) {
        this.$buefy.toast.open({
          message: regex.promptKey.message,
          type: 'is-danger'
        });
      } else if (this.doesKeyExist(this.newPromptKey)) {
        this.$buefy.toast.open({
          message: 'Prompt with same key already exists!',
          type: 'is-danger'
        });
      } else {
        Vue.set(this.prompts, this.newPromptKey, {
          message: '',
          answers: []
        });
        this.newPromptKey = '';
        this.isAddingPrompt = false;
      }
    },
    deletePromptHandler(key) {
      this.$buefy.dialog.confirm({
        message: `Proceeding will delete this prompt and can't be undone?`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          if (this.selectedPromptKey === key) this.selectedPromptKey = null;
          Vue.delete(this.prompts, key);
          this.$buefy.toast.open(`Deleted the prompt '${key}' successfully!`);
        }
      });
    },
    doesKeyExist(key) {
      return this.prompts.hasOwnProperty(key);
    },
    editKeyHandler() {
      const oldKey = this.selectedPromptKey;
      const newKey = this.editingPromptKey;

      if (oldKey === newKey) {
        this.isEditingKey = false;
        return;
      }

      if (!this.isKeyValid(newKey)) {
        this.$buefy.toast.open({
          message: regex.promptKey.message,
          type: 'is-danger'
        });
      } else if (this.doesKeyExist(newKey)) {
        this.$buefy.toast.open({
          message: 'Prompt with same key already exists!',
          type: 'is-danger'
        });
      } else {
        this.updateAnswerKeys(oldKey, newKey);
        Vue.set(this.prompts, newKey, this.prompts[oldKey]);
        this.selectedPromptKey = newKey;
        Vue.delete(this.prompts, oldKey);
        this.isEditingKey = false;
        this.$buefy.toast.open(
          'Prompt and all its references successfully updated!'
        );
      }
    },
    getDefinition() {
      return cloneDeep(this.definition);
    },
    isKeyValid(key) {
      return regex.promptKey.pattern.test(key);
    },
    openKeyEditor() {
      this.editingPromptKey = this.selectedPromptKey;
      this.isEditingKey = true;
    },
    updateAnswerKeys(oldKey, newKey) {
      function updateHelper(arr) {
        if (!Array.isArray(arr)) return;

        arr.forEach(o => {
          const [flowName, promptKey] = o.nextKey.split('.');
          if (!flowName && promptKey === oldKey) {
            o.nextKey = `${flowName}.${newKey}`;
          }
        });
      }

      // Update in sharedAnswers
      forOwn(this.answers, v => updateHelper(v));
      // Update in prompts
      forOwn(this.prompts, v => updateHelper(v.answers));
    },
    updateDefinitionHandler() {
      const definition = cloneDeep(this.definition);
      this.onSubmit(definition);
    },
    updatePromptHandler({ data, key }) {
      this.prompts[key] = data;
    },
    updateSharedAnswerIdHandler(oldId, newId) {
      forOwn(this.prompts, v => {
        if (Array.isArray(v.answers)) return;
        if (v.answers === oldId) v.answers = newId;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.editor {
  border: 1px solid $border-color;
  border-radius: $border-radius;
  box-shadow: $light-background-shadow;

  &__header {
    display: flex;
    align-items: center;
    background-color: $app-header-alt-background;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid $border-color-dark;
    min-height: $flow-editor-header-min-height;
  }

  &__title {
    flex: 1;
  }

  &__body {
    position: relative;
  }

  &__key {
    display: flex;
    align-items: center;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid $border-color;
    cursor: pointer;

    &:hover {
      background-color: rgba($secondary-color, 0.5);
    }

    &--active {
      background-color: $secondary-color !important;
    }
  }

  &__key-title {
    flex: 1 0 0;
    margin-right: 0.5rem;
  }

  &__key-trailer {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    margin: 0 0.7rem;
  }

  &__add-prompt {
    padding: 0.5rem;
  }

  &__edit-key {
    margin-left: 0.2rem;
  }

  &__hint {
    flex: 1 0 0;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
    text-align: center;

    .content {
      margin-left: 0.5rem;
    }
  }
}
</style>
